import React, { useState, useEffect, useRef } from "react";
import { X, Info, Building, Mail, Phone, User, CheckCircle2, Loader2, ChevronRight, ChevronLeft, Building2, AlertCircle } from "lucide-react";
import { useApp } from "../../contexts/AppContext";
import { useTheme } from "../../contexts/ThemeContext";
import { trainersAPI } from "../../services/api";

// Normalize stored/displayed role values. Older records may carry "organization_manager".
const normalizeRole = (r) => (r === "organization_manager" ? "manager" : r);
const isToggleableRole = (r) => {
  const n = normalizeRole(r);
  return n === "trainer" || n === "manager";
};

const INITIAL_STATE = {
  name: "",
  email: "",
  phone: "",
  role: "trainer",
  privateCommissionPercentage: "",
  groupCommissionPercentage: "",
  salaryDate: "15",
  color: "#69306D",
  bloodType: "",
  tcNo: "",
  gender: "",
  weight: "",
  age: "",
  height: "",
  profilePicture: "",
  notes: "",
  status: "active",
  assignedOrganizations: [],
  preferredHours: {
    monday: { start: "", end: "" },
    tuesday: { start: "", end: "" },
    wednesday: { start: "", end: "" },
    thursday: { start: "", end: "" },
    friday: { start: "", end: "" },
    saturday: { start: "", end: "" },
    sunday: { start: "", end: "" },
  },
};

const TrainerModal = ({
  isOpen,
  onClose,
  onSubmit,
  trainer,
  title,
  loading = false,
}) => {
  const { colors } = useTheme();
  const {
    organizations,
    selectedOrganizationId,
    isSuperAdmin,
    canAssignTrainers,
  } = useApp();

  const [formData, setFormData] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("organization");
  const [roleChanging, setRoleChanging] = useState(false);
  const originalRoleRef = useRef(null);

  // Filter out 'all' organization from the list
  const filteredOrganizations = organizations.filter((org) => org.id !== "all");

  // Determine if organizations tab should be shown
  const showOrganizationsTab = isSuperAdmin() && canAssignTrainers();

  // Define tabs
  const tabs = showOrganizationsTab
    ? [
      { id: "organization", name: "Organizasyonlar" },
      { id: "basic", name: "Temel Bilgiler" },
      { id: "details", name: "Detaylar" },
      { id: "schedule", name: "Çalışma Saatleri" },
    ]
    : [
      { id: "basic", name: "Temel Bilgiler" },
      { id: "details", name: "Detaylar" },
      { id: "schedule", name: "Çalışma Saatleri" },
    ];

  useEffect(() => {
    if (!isOpen) return;

    if (trainer) {
      // Collect IDs from both assignedOrganizations (list) and organizationId (singular)
      const rawOrgs = trainer.assignedOrganizations || [];
      const orgIdsFromList = rawOrgs.map(org => typeof org === 'object' ? org.id : org);
      
      const allOrgIds = [
        ...orgIdsFromList,
        trainer.organizationId
      ].filter(Boolean);
      
      // Deduplicate
      const normalizedOrgs = Array.from(new Set(allOrgIds));

      const normalizedRole = normalizeRole(trainer.role) || "trainer";
      setFormData({
        ...INITIAL_STATE,
        ...trainer,
        role: normalizedRole,
        privateCommissionPercentage: trainer.privateCommissionPercentage ?? "",
        groupCommissionPercentage: trainer.groupCommissionPercentage ?? "",
        salaryDate: trainer.salaryDate || "15",
        assignedOrganizations: normalizedOrgs.length > 0
          ? normalizedOrgs
          : (selectedOrganizationId && selectedOrganizationId !== 'all' ? [selectedOrganizationId] : []),
        preferredHours: trainer.preferredHours || INITIAL_STATE.preferredHours,
      });
      originalRoleRef.current = normalizedRole;
      setActiveTab("basic");

      // Refetch this trainer so we pick up the authoritative Cognito-backed role
      // and assignedOrganizations from the backend (list data may be stale).
      const orgForFetch = trainer.organizationId
        || (selectedOrganizationId && selectedOrganizationId !== 'all' ? selectedOrganizationId : null);
      if (orgForFetch && trainer.id) {
        trainersAPI
          .getById(orgForFetch, trainer.id)
          .then((res) => {
            const fresh = res?.data?.data;
            if (!fresh) return;
            const freshRole = normalizeRole(fresh.role) || normalizedRole;
            const freshOrgs = Array.isArray(fresh.assignedOrganizations)
              ? fresh.assignedOrganizations.map((o) => typeof o === 'object' ? o?.id : o).filter(Boolean)
              : null;
            setFormData((prev) => ({
              ...prev,
              role: freshRole,
              assignedOrganizations: freshOrgs && freshOrgs.length > 0 ? freshOrgs : prev.assignedOrganizations,
            }));
            originalRoleRef.current = freshRole;
          })
          .catch((err) => {
            console.warn('Failed to refetch trainer for role enrichment:', err);
          });
      }
    } else {
      setFormData({
        ...INITIAL_STATE,
        assignedOrganizations: selectedOrganizationId && selectedOrganizationId !== "all"
          ? [selectedOrganizationId]
          : [],
      });
      originalRoleRef.current = "trainer";
      setActiveTab(showOrganizationsTab ? "organization" : "basic");
    }
    setErrors({});
  }, [isOpen, trainer, selectedOrganizationId]);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === "organization") {
      if (!formData.assignedOrganizations || formData.assignedOrganizations.length === 0) {
        newErrors.assignedOrganizations = "En az bir organizasyon seçilmelidir";
      }
    }

    if (step === "basic") {
      if (!formData.name.trim()) newErrors.name = "Ad Soyad gereklidir";
      if (!formData.email.trim()) {
        newErrors.email = "E-posta gereklidir";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Geçerli bir e-posta adresi girin";
      }
      if (!formData.phone.trim()) newErrors.phone = "Telefon gereklidir";
    }

    if (step === "details") {
      if (formData.height && (isNaN(formData.height) || formData.height <= 0)) {
        newErrors.height = "Boy pozitif bir sayı olmalıdır";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (validateStep(activeTab)) {
      const currentIndex = tabs.findIndex((t) => t.id === activeTab);
      if (currentIndex < tabs.length - 1) {
        setActiveTab(tabs[currentIndex + 1].id);
      }
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    const currentIndex = tabs.findIndex((t) => t.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isOnLastTab = activeTab === tabs[tabs.length - 1].id;
    if (!isOnLastTab) return;

    let allValid = true;
    for (const tab of tabs) {
      if (!validateStep(tab.id)) {
        allValid = false;
        setActiveTab(tab.id);
        break;
      }
    }
    if (!allValid) return;

    try {
      const {
        trainerCommissionPercentage, PK, SK, GSI1PK, GSI1SK, EntityType, createdAt, updatedAt,
        role: roleFromForm,
        ...cleanData
      } = formData;

      // Generic update never touches the role — role changes are handled by the
      // dedicated /role endpoint (which also syncs Cognito groups + MANAGER records).
      const submitData = {
        ...cleanData,
        privateCommissionPercentage: cleanData.privateCommissionPercentage ? Number(cleanData.privateCommissionPercentage) : 0,
        groupCommissionPercentage: cleanData.groupCommissionPercentage ? Number(cleanData.groupCommissionPercentage) : 0,
      };

      await onSubmit(submitData);

      // Apply role change as a second call, but only when editing an existing trainer,
      // the role is toggleable, and it actually changed.
      const isEditing = !!trainer?.id;
      const desiredRole = normalizeRole(roleFromForm);
      const originalRole = originalRoleRef.current;
      const roleChanged = isEditing && isToggleableRole(desiredRole) && desiredRole !== originalRole;

      if (roleChanged) {
        const orgId = trainer.organizationId || selectedOrganizationId;
        if (!orgId || orgId === "all") {
          setErrors((prev) => ({
            ...prev,
            submit: "Rol değişikliği için aktif bir organizasyon seçili olmalı.",
          }));
          return;
        }
        try {
          setRoleChanging(true);
          await trainersAPI.changeRole(orgId, trainer.id, desiredRole);
          originalRoleRef.current = desiredRole;
        } catch (err) {
          const msg = err?.response?.data?.error || err?.message || "Rol değiştirilemedi.";
          setErrors((prev) => ({ ...prev, submit: msg }));
          console.error("Role change failed:", err);
          return;
        } finally {
          setRoleChanging(false);
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleOrganizationChange = (organizationId, isChecked) => {
    setFormData((prev) => ({
      ...prev,
      assignedOrganizations: isChecked
        ? [...prev.assignedOrganizations, organizationId]
        : prev.assignedOrganizations.filter((id) => id !== organizationId),
    }));
    if (errors.assignedOrganizations) setErrors((prev) => ({ ...prev, assignedOrganizations: "" }));
  };

  const handlePreferredHoursChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      preferredHours: { ...prev.preferredHours, [day]: { ...prev.preferredHours[day], [field]: value } },
    }));
  };

  if (!isOpen) return null;

  const days = [
    { key: "monday", label: "Pazartesi" },
    { key: "tuesday", label: "Salı" },
    { key: "wednesday", label: "Çarşamba" },
    { key: "thursday", label: "Perşembe" },
    { key: "friday", label: "Cuma" },
    { key: "saturday", label: "Cumartesi" },
    { key: "sunday", label: "Pazar" },
  ];

  const isLastTab = activeTab === tabs[tabs.length - 1].id;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
      <div className={`w-full max-w-2xl bg-white dark:bg-[#121214] rounded-2xl shadow-2xl flex flex-col border border-zinc-200 dark:border-zinc-800 max-h-[90vh] overflow-hidden`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-100 dark:border-zinc-800 shrink-0">
          <h2 className={`text-xl font-bold ${colors.textPrimary}`}>{title}</h2>
          <button onClick={onClose} className={`p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors`}>
            <X className={`w-5 h-5 ${colors.textMuted}`} />
          </button>
        </div>

        {/* Clean Tabs */}
        <div className="flex px-6 border-b border-zinc-100 dark:border-zinc-800 shrink-0 overflow-x-auto no-scrollbar">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-4 text-sm font-semibold transition-all relative whitespace-nowrap ${isActive ? 'text-blue-600 dark:text-blue-500' : `${colors.textMuted} hover:text-zinc-600`}`}
              >
                {tab.name}
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-500 rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          <div className="space-y-6">
            {activeTab === "organization" && (
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Building2 className="w-5 h-5 text-blue-500" />
                  <h3 className={`font-bold ${colors.textPrimary}`}>Organizasyon Seçimi</h3>
                </div>
                <div className={`grid grid-cols-1 gap-2 max-h-[400px] overflow-y-auto p-1`}>
                  {filteredOrganizations.map((org) => (
                    <label key={org.id} className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer ${formData.assignedOrganizations.includes(org.id) ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/5' : 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-300'}`}>
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${formData.assignedOrganizations.includes(org.id) ? 'bg-blue-500 text-white' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500'}`}>
                          <Building className="w-5 h-5" />
                        </div>
                        <div>
                          <p className={`font-bold text-sm ${colors.textPrimary}`}>{org.name}</p>
                          <p className={`text-xs ${colors.textMuted}`}>{org.address}</p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.assignedOrganizations.includes(org.id)}
                        onChange={(e) => handleOrganizationChange(org.id, e.target.checked)}
                        className="w-5 h-5 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  ))}
                </div>
                {errors.assignedOrganizations && <p className="text-xs font-bold text-red-500">{errors.assignedOrganizations}</p>}
              </div>
            )}

            {activeTab === "basic" && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className={`text-sm font-semibold ${colors.textSecondary}`}>Ad Soyad *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border ${errors.name ? 'border-red-500' : 'border-zinc-300 dark:border-zinc-700'} rounded-xl focus:border-blue-500 outline-none`}
                    placeholder="Ad Soyad girin"
                  />
                  {errors.name && <p className="text-xs font-bold text-red-500">{errors.name}</p>}
                </div>

                <div className="space-y-2">
                  <label className={`text-sm font-semibold ${colors.textSecondary}`}>E-posta *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border ${errors.email ? 'border-red-500' : 'border-zinc-300 dark:border-zinc-700'} rounded-xl focus:border-blue-500 outline-none transition-all`}
                    placeholder="E-posta adresi girin"
                  />
                  {errors.email && <p className="text-xs font-bold text-red-500">{errors.email}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className={`text-sm font-semibold ${colors.textSecondary}`}>Telefon *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border ${errors.phone ? 'border-red-500' : 'border-zinc-300 dark:border-zinc-700'} rounded-xl focus:border-blue-500 outline-none`}
                      placeholder="+90..."
                    />
                    {errors.phone && <p className="text-xs font-bold text-red-500">{errors.phone}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className={`text-sm font-semibold ${colors.textSecondary}`}>Rol</label>
                    {(() => {
                      const isEditing = !!trainer?.id;
                      const toggleable = isToggleableRole(formData.role);
                      const disabled = !isEditing || !toggleable || roleChanging;
                      return (
                        <>
                          <select
                            name="role"
                            value={normalizeRole(formData.role) || "trainer"}
                            onChange={handleChange}
                            disabled={disabled}
                            className={`w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-xl outline-none focus:border-blue-500 font-medium ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                          >
                            <option value="trainer">Eğitmen</option>
                            <option value="manager">Yönetici</option>
                            {formData.role && !isToggleableRole(formData.role) && (
                              <option value={formData.role}>{formData.role}</option>
                            )}
                          </select>
                          {!isEditing && (
                            <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
                              Yeni eğitmenler "Eğitmen" olarak oluşturulur. Oluşturduktan sonra düzenleme ekranından yönetici yapılabilir.
                            </p>
                          )}
                          {isEditing && !toggleable && (
                            <p className="text-[10px] text-amber-600 dark:text-amber-400">
                              Bu hesabın rolü bu ekrandan değiştirilemez (örn. super admin).
                            </p>
                          )}
                          {isEditing && toggleable && originalRoleRef.current && normalizeRole(formData.role) !== originalRoleRef.current && (
                            <p className="text-[10px] text-blue-600 dark:text-blue-400">
                              Kaydet'e bastığında {normalizeRole(formData.role) === "manager" ? "yönetici" : "eğitmen"} rolüne geçecek.
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-zinc-50 dark:bg-zinc-900 rounded-2xl border border-zinc-100 dark:border-zinc-800">
                  <div className="space-y-1.5">
                    <label className={`text-xs font-bold text-zinc-500 uppercase tracking-widest`}>Bireysel Komisyon (%)</label>
                    <input
                      type="number"
                      name="privateCommissionPercentage"
                      value={formData.privateCommissionPercentage}
                      onChange={handleChange}
                      className="w-full px-3 py-2 bg-transparent border-b border-zinc-200 dark:border-zinc-700 outline-none focus:border-blue-500 font-bold"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className={`text-xs font-bold text-zinc-500 uppercase tracking-widest`}>Grup Komisyon (%)</label>
                    <input
                      type="number"
                      name="groupCommissionPercentage"
                      value={formData.groupCommissionPercentage}
                      onChange={handleChange}
                      className="w-full px-3 py-2 bg-transparent border-b border-zinc-200 dark:border-zinc-700 outline-none focus:border-blue-500 font-bold"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className={`text-sm font-semibold ${colors.textSecondary}`}>Maaş Günü</label>
                    <select
                      name="salaryDate"
                      value={formData.salaryDate}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-xl outline-none"
                    >
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <option key={day} value={day.toString()}>Ayın {day}. günü</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className={`text-sm font-semibold ${colors.textSecondary}`}>Hesap Durumu</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-xl outline-none"
                    >
                      <option value="active">Aktif</option>
                      <option value="inactive">Pasif</option>
                      <option value="suspended">Askıda</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "details" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-2">
                    <label className={`text-sm font-semibold ${colors.textSecondary}`}>Cinsiyet</label>
                    <select name="gender" value={formData.gender} onChange={handleChange} className="w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-xl outline-none">
                      <option value="">Seçiniz</option>
                      <option value="Male">Erkek</option>
                      <option value="Female">Kadın</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className={`text-sm font-semibold ${colors.textSecondary}`}>Yaş</label>
                    <input type="number" name="age" value={formData.age} onChange={handleChange} className="w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-xl outline-none" placeholder="25" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-2">
                    <label className={`text-sm font-semibold ${colors.textSecondary}`}>Kilo (kg)</label>
                    <input type="number" name="weight" value={formData.weight} onChange={handleChange} className="w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-xl outline-none" placeholder="70" />
                  </div>
                  <div className="space-y-2">
                    <label className={`text-sm font-semibold ${colors.textSecondary}`}>Boy (cm)</label>
                    <input type="number" name="height" value={formData.height} onChange={handleChange} className={`w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border ${errors.height ? 'border-red-500' : 'border-zinc-300 dark:border-zinc-700'} rounded-xl focus:border-blue-500 outline-none`} placeholder="180" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className={`text-sm font-semibold ${colors.textSecondary}`}>TC Kimlik No</label>
                  <input type="text" name="tcNo" value={formData.tcNo} onChange={handleChange} className="w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-xl outline-none" placeholder="11 haneli TC No" />
                </div>

                <div className="space-y-2">
                  <label className={`text-sm font-semibold ${colors.textSecondary}`}>Notlar</label>
                  <textarea name="notes" rows="3" value={formData.notes} onChange={handleChange} className="w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-xl outline-none resize-none" placeholder="Ek notlar..." />
                </div>
              </div>
            )}

            {activeTab === "schedule" && (
              <div className="space-y-4">
                <div className="flex items-center space-x-2 pb-2 border-b border-zinc-100 dark:border-zinc-800">
                  <Info className="w-4 h-4 text-blue-500" />
                  <p className={`text-xs font-bold ${colors.textMuted} uppercase tracking-wider`}>Planlanan Çalışma Saatleri</p>
                </div>
                <div className="space-y-2">
                  {days.map((day) => (
                    <div key={day.key} className="grid grid-cols-12 gap-4 items-center p-2 hover:bg-zinc-50 dark:hover:bg-zinc-900 rounded-xl transition-colors">
                      <div className={`col-span-4 text-sm font-bold ${colors.textSecondary}`}>
                        {day.label}
                      </div>
                      <div className="col-span-8 flex items-center space-x-2">
                        <input
                          type="time"
                          value={formData.preferredHours[day.key].start}
                          onChange={(e) => handlePreferredHoursChange(day.key, "start", e.target.value)}
                          className="flex-1 px-3 py-1.5 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg text-xs font-bold"
                        />
                        <span className="text-zinc-400">—</span>
                        <input
                          type="time"
                          value={formData.preferredHours[day.key].end}
                          onChange={(e) => handlePreferredHoursChange(day.key, "end", e.target.value)}
                          className="flex-1 px-3 py-1.5 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg text-xs font-bold"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error banner */}
        {errors.submit && (
          <div className="mx-6 mt-4 mb-0 p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700 dark:text-red-300">{errors.submit}</p>
          </div>
        )}

        {/* Footer Actions */}
        <div className="p-6 border-t border-zinc-100 dark:border-zinc-800 flex justify-between items-center bg-zinc-50/50 dark:bg-zinc-900/10 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 rounded-lg border border-zinc-300 dark:border-zinc-700 text-sm font-bold hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
          >
            İptal
          </button>

          <div className="flex items-center space-x-3">
            {activeTab !== tabs[0].id && (
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center space-x-1 px-4 py-2.5 text-sm font-bold text-zinc-600 dark:text-zinc-400 hover:text-blue-500"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Geri</span>
              </button>
            )}

            {!isLastTab ? (
              <button
                type="button"
                onClick={handleNext}
                className="flex items-center space-x-1 px-6 py-2.5 bg-blue-600 dark:bg-blue-500 text-white rounded-lg text-sm font-bold hover:opacity-90 transition-opacity"
              >
                <span>İleri</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading || roleChanging}
                className="px-8 py-2.5 rounded-lg bg-blue-600 dark:bg-blue-500 text-white text-sm font-bold hover:opacity-90 transition-opacity flex items-center space-x-2 disabled:opacity-50"
              >
                {(loading || roleChanging) ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                <span>{trainer?.id ? 'Eğitmeni Güncelle' : 'Eğitmen Oluştur'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainerModal;
